import { Tooltip } from '@material-ui/core';
import moment from 'moment';
import React, { useState } from 'react';
import { CalendarV2 } from '~/src/dash/components/calendar/CalendarV2';
import { INewCalDates } from '~/src/dash/components/calendar/CalendarV2/common';
import { IDeliveryVariables } from '~/src/dash/apiV2/forecasting/forecasting';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { Col, Cols, spacing } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  DateRange,
  defaultForecastingDateRanges,
  findForecastingDateRange,
} from '~/src/dash/utils/dateTimeRanges';
import { DateTimeRangeToggles } from '~/src/dash/views/dashboards/DashboardHome/common/DateTimeRangeToggles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { generateCustomDateRange } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { unstable_batchedUpdates } from 'react-dom';
import { track } from '~/src/dash/analytics/analytics';

interface IProps {
  deliveryVariables: IDeliveryVariables;
}

export const TimeRangeAdjustment = ({ deliveryVariables }: IProps): JSX.Element => {
  const { user, isSuperUser } = useBaseStore();
  const [forecastStartDate, setForecastStartDate] = useState(deliveryVariables.ForecastedStartDate);
  const [forecastEndDate, setForecastEndDate] = useState(deliveryVariables.ForecastedEndDate);

  // when user updates the date range using the date toggle, update the query
  const onDateTimeRangeTogglesUpdate = (rangeKey: DateRange): void => {
    const newDateRange = findForecastingDateRange(rangeKey);
    const { startDate, endDate } = newDateRange.generateQueryDates(user.publisher.Timezone);

    deliveryVariables.ForecastedStartDate = startDate;
    deliveryVariables.ForecastedEndDate = endDate;
    unstable_batchedUpdates(() => {
      setForecastStartDate(startDate);
      setForecastEndDate(endDate);
    });
  };

  // helper function to find if there is any matched preset value for the datetime toggle when
  // user updates time range using the calendar
  const findDateTimeRangeToggleValue = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ): DateRange => {
    if (!startDate || !endDate) {
      return defaultForecastingDateRanges[0].key as DateRange;
    }
    const dateRange = defaultForecastingDateRanges.find((range) =>
      range.isWithinRange(
        moment(startDate.clone()).startOf('day'),
        moment(endDate.clone()).endOf('day'),
        user.publisher.Timezone,
      ),
    );
    return (
      (dateRange?.key as DateRange) || generateCustomDateRange(endDate.diff(startDate, 'days')).key
    );
  };

  const onCalendarUpdate = ({ newStartDate, newEndDate }: INewCalDates): void => {
    deliveryVariables.ForecastedStartDate = moment(newStartDate)
      .tz(user.publisher.Timezone)
      .startOf('day');
    deliveryVariables.ForecastedEndDate = moment(newEndDate)
      .tz(user.publisher.Timezone)
      .endOf('day');
    track('Forecast - Calendar', { isSuperUser }, user.user);
    unstable_batchedUpdates(() => {
      setForecastStartDate(deliveryVariables.ForecastedStartDate);
      setForecastEndDate(deliveryVariables.ForecastedEndDate);
    });
  };

  return (
    <Cols
      gap={spacing.large}
      style={{
        alignItems: 'center',
      }}
    >
      <Col>
        <CalendarV2
          calStartDate={forecastStartDate}
          calEndDate={forecastEndDate}
          handleApplyDatesChange={onCalendarUpdate}
          uniqueID='forecastingCal'
          maxDate={moment().tz(user.publisher.Timezone).add(1, 'year').endOf('day')}
          minDate={moment().tz(user.publisher.Timezone).startOf('day')}
        />
      </Col>
      <Col>
        <DateTimeRangeToggles
          ranges={defaultForecastingDateRanges}
          activeKey={findDateTimeRangeToggleValue(forecastStartDate, forecastEndDate)}
          onClick={(rangeKey: DateRange) => {
            track('Forecast - Select Date Shortcut', { isSuperUser, rangeKey }, user.user);
            onDateTimeRangeTogglesUpdate(rangeKey);
          }}
        />
      </Col>
      <Col>
        <Tooltip title='Forecast is extrapolated based on trends from Last 30 days' placement='top'>
          <div style={{ display: 'flex' }}>
            <IconV2 large name='info-static' color={publicaColors.gray} />
          </div>
        </Tooltip>
      </Col>
    </Cols>
  );
};
